import React, { useEffect, useState } from 'react'
import { useFieldArray, UseFieldArrayReturn, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting'
import { FormFields } from '~/components/FormFields'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { Button, Divider, Form, Header } from 'semantic-ui-react'
import { handleErrors } from '~/utils/helpers'
import dayjs from 'dayjs'
import { MilkTestCard, MilkTestType } from './MilkTestCard'
import { MilkTestsWrapper, ResponsiveGrid } from './style'
import MilkTestEditDrawer from './MilkTestEditDrawer'

type FormValuesType = {
  milkTests?: MilkTestType[]
  [key: string]: any
}

const EditInvoice: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const navigate = useNavigate()

  const [milkClassOptions, setMilkClassOptions] = useState([])
  const [selectedTest, setSelectedTest] = useState<MilkTestType | null>(null);
  const [isVisibleMilkTestDrawer, setIsVisibleMilkTestDrawer] = useState(false)

  const { control, handleSubmit, reset, getValues } = useForm({
    mode: 'onSubmit'
  })

  const pseudomonadsTests = useWatch({ control, name: 'pseudomonadsTest' })
  const watchMilkQualityTypes = useWatch({ control, name: 'milkQualityUpdateTypes' })
  const watchMilkType = useWatch({ control, name: 'milkQualityUpdateType' })
  const milkTests: UseFieldArrayReturn<FormValuesType, 'milkTests', 'key'> = useFieldArray({
    control,
    name: 'milkTests',
    keyName: 'key'
  })

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await MilkMoneyAccountingService.editInvoice(id)
      setMilkClassOptions(response.data?.milkClasses.map(milkClass => ({ value: milkClass.id, text: milkClass.name })))
      reset({
        ...response.data,
        milkQualityUpdateType: response.data?.milkQualityUpdateTypes[0]?.key.toString()
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      await MilkMoneyAccountingService.updateInvoice(id, data)
      notify(t('Successfully update invoice'), 'success')
      navigate(`${RouteTypes.MILK_MONEY_INVOICE}#${initialDate}`)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderForm() {
    return <>
      <FormFields.Select
        control={control}
        name={'milkQualityUpdateType'}
        disableScroolIntoView
        label={t('Milk Quality Update Type')}
        rules={{
          required: t('Please select milk quality update type')
        }}
        selectOnBlur={false}
        placeholder={t('Milk Quality Update Type')}
        options={watchMilkQualityTypes?.map(type => ({ value: type.key.toString(), text: type.label }))}
      />
      <Form.Group widths={'equal'}>
        {watchMilkType !== '0' && <FormFields.Select
          control={control}
          name={'milkQualityClassId'}
          label={t('Grade')}
          rules={{
            required: t('Please select milk quaility')
          }}
          selectOnBlur={false}
          placeholder={t('Change quality class')}
          options={milkClassOptions}
        />}
        <FormFields.Input
          control={control}
          formatNumberForDb
          label={t('3-month cell content (Geo.-MW)')}
          placeholder={t('Enter cell count')}
          typeInput={'controled'}
          rules={{
            required: t('Please enter cell count'),
            min: {
              value: 0,
              message: t('Min. value is 0')
            }
          }}
          name={'cellCountLastMonths'}
        />
        <FormFields.Input
          control={control}
          formatNumberForDb
          label={t('2-month germ count (Geo.-MW)')}
          placeholder={t('Enter germ count')}
          typeInput={'controled'}
          rules={{
            required: t('Please enter germ count'),
            min: {
              value: 0,
              message: t('Min. value is 0')
            }
          }}
          name={'germCountLastMonths'}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          formatNumberForDb
          label={t('Cell Count')}
          placeholder={t('Enter Cell Count')}
          typeInput={'controled'}
          rules={{
            required: t('Please enter Cell Count'),
            min: {
              value: 0,
              message: t('Min. value is 0')
            }
          }}
          name={'cellCount'}
        />
        <FormFields.Input
          control={control}
          formatNumberForDb
          label={t('Germ Count')}
          placeholder={t('Enter Germ Count')}
          typeInput={'controled'}
          rules={{
            required: t('Please enter Germ Count'),
            min: {
              value: 0,
              message: t('Min. value is 0')
            }
          }}
          name={'germCount'}
        />
        {pseudomonadsTests?.value && <Form.Field>
          <label>{t('Pseudomonas test results ({{date}})', { date: dayjs(pseudomonadsTests?.date).format('DD.MM.YYYY') })}</label>
          <div style={{ padding: '.67857143em 1em' }}>{pseudomonadsTests?.value}</div>
        </Form.Field>}
      </Form.Group>
    </>
  }

  function renderMilkTests() {
    return <MilkTestsWrapper>
      <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', marginBottom: 5 }}>
        <Header>{t('Milk tests')}</Header>
        <Button
          onClick={() => setIsVisibleMilkTestDrawer(true)}
          style={{ color: 'white' }}
          type='button'
        >
          {t('Add milk test')}</Button>
      </div>
      <Divider/>
      <ResponsiveGrid>
        {milkTests.fields?.map(milkTest => {
          return <MilkTestCard
            key={milkTest.key}
            test={milkTest}
            onEdit={() => {
              setIsVisibleMilkTestDrawer(true)
              setSelectedTest(milkTest)
            }}
            isSelected={selectedTest?.id === milkTest.id}
          />
        })}
      </ResponsiveGrid>
    </MilkTestsWrapper>
  }

  function renderCreateEditMilkTest() {
    return <MilkTestEditDrawer
      isVisible={isVisibleMilkTestDrawer}
      initialDate={getValues('date')}
      inhibitorOptions={getValues('inhibitorValues') || []}
      handleHiddenDrawer={() => {
        setIsVisibleMilkTestDrawer(false)
        setSelectedTest(null)
      }}
      handleSubmitMilkTest={(data, id) => {
        if(id) {
          const index = milkTests.fields.findIndex(field => field.id === id)
          if(index >= 0) {
            milkTests.update(index, data)
          }
        } else {
          milkTests.append(data)
        }
        setIsVisibleMilkTestDrawer(false)
        setSelectedTest(null)
      }}
      milkTestData={selectedTest}
    />
  }

  const initialDate = getValues('date')

  return <Layout
    title={t('Adjust quality rating')}
    segmented
    isBusy={isBusy}
    close={`${RouteTypes.MILK_MONEY_INVOICE}#${initialDate}`}
    action={t('Update')}
    onSubmit={handleSubmit(onSubmit)}
  >
    {renderCreateEditMilkTest()}
    {renderMilkTests()}
    {renderForm()}
  </Layout>
}
export default EditInvoice